import React, { useState, useEffect } from 'react';
import { alertAPI } from '../services/api';
import { X, Save, AlertTriangle, Settings } from 'lucide-react';
import toast from 'react-hot-toast';

interface ThresholdSettingsProps {
  onClose: () => void;
}

const ThresholdSettings: React.FC<ThresholdSettingsProps> = ({ onClose }) => {
  const [moistureMin, setMoistureMin] = useState(30);
  const [moistureMax, setMoistureMax] = useState(70);
  const [cooldownMinutes, setCooldownMinutes] = useState(30);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchThresholds();
  }, []);

  const fetchThresholds = async () => {
    try {
      const response = await alertAPI.getThresholds();
      const data = response.data;
      setMoistureMin(data.moistureMin);
      setMoistureMax(data.moistureMax);
      setCooldownMinutes(data.cooldownMinutes);
    } catch (error) {
      console.error('Erreur lors de la récupération des seuils:', error);
    }
  };

  const handleSave = async () => {
    if (moistureMin >= moistureMax) {
      toast.error('Le seuil minimum doit être inférieur au seuil maximum');
      return;
    }

    try {
      setLoading(true);
      await alertAPI.updateThresholds(null, {
        moistureMin,
        moistureMax,
        cooldownMinutes
      });
      toast.success('Seuils mis à jour avec succès');
      onClose();
    } catch (error) {
      toast.error('Échec de la mise à jour des seuils');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div 
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '1rem',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          padding: '2rem',
          maxWidth: '32rem',
          width: '90%',
          animation: 'slideUp 0.3s ease-out',
          border: '1px solid var(--border-light)'
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
            <div style={{
              padding: '0.75rem',
              borderRadius: '0.75rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
              <Settings style={{width: '1.5rem', height: '1.5rem', color: 'white'}} />
            </div>
            <div>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: 'var(--text-primary)'
              }}>
                Configuration des Alertes
              </h3>
              <p style={{
                margin: 0,
                fontSize: '0.875rem',
                color: 'var(--text-secondary)'
              }}>
                Définissez les seuils d'humidité du sol
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem',
              borderRadius: '0.5rem',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              color: 'var(--text-secondary)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X style={{width: '1.25rem', height: '1.25rem'}} />
          </button>
        </div>

        <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem'}}>
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '0.5rem'
            }}>
              <span>🔴</span>
              <span>Seuil d'Humidité Minimum (%)</span>
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={moistureMin}
              onChange={(e) => setMoistureMin(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '2px solid var(--border-light)',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                fontWeight: '600',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#ef4444'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-light)'}
            />
            <p style={{
              marginTop: '0.375rem',
              fontSize: '0.75rem',
              color: 'var(--text-secondary)'
            }}>
              ⚠️ Alerte critique quand l'humidité descend sous cette valeur
            </p>
          </div>

          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '0.5rem'
            }}>
              <span>🟢</span>
              <span>Seuil d'Humidité Maximum (%)</span>
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={moistureMax}
              onChange={(e) => setMoistureMax(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '2px solid var(--border-light)',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                fontWeight: '600',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#10b981'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-light)'}
            />
            <p style={{
              marginTop: '0.375rem',
              fontSize: '0.75rem',
              color: 'var(--text-secondary)'
            }}>
              💧 Alerte d'avertissement quand l'humidité dépasse cette valeur
            </p>
          </div>

          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '0.5rem'
            }}>
              <span>⏱️</span>
              <span>Cooldown des Alertes (minutes)</span>
            </label>
            <input
              type="number"
              min="1"
              max="1440"
              value={cooldownMinutes}
              onChange={(e) => setCooldownMinutes(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '2px solid var(--border-light)',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                fontWeight: '600',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#f59e0b'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-light)'}
            />
            <p style={{
              marginTop: '0.375rem',
              fontSize: '0.75rem',
              color: 'var(--text-secondary)'
            }}>
              🔔 Temps minimum entre deux alertes similaires (évite le spam)
            </p>
          </div>

          {/* Visual Indicator */}
          <div style={{
            padding: '1.25rem',
            borderRadius: '0.75rem',
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem'}}>
              <AlertTriangle style={{width: '1.125rem', height: '1.125rem', color: '#f59e0b'}} />
              <span style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--text-primary)'
              }}>
                Aperçu des Zones d'Alerte
              </span>
            </div>
            <div style={{
              position: 'relative',
              height: '3rem',
              background: 'linear-gradient(to right, #ef4444 0%, #fbbf24 50%, #10b981 100%)',
              borderRadius: '0.5rem',
              overflow: 'hidden'
            }}>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  width: `${moistureMin}%`
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  width: `${100 - moistureMax}%`
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '3px',
                  height: '100%',
                  backgroundColor: 'white',
                  boxShadow: '0 0 8px rgba(0,0,0,0.5)',
                  left: `${moistureMin}%`
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '3px',
                  height: '100%',
                  backgroundColor: 'white',
                  boxShadow: '0 0 8px rgba(0,0,0,0.5)',
                  left: `${moistureMax}%`
                }}
              />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: `${moistureMin}%`,
                transform: 'translate(-50%, -150%)',
                backgroundColor: 'white',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: '700',
                color: '#ef4444',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {moistureMin}%
              </div>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: `${moistureMax}%`,
                transform: 'translate(-50%, -150%)',
                backgroundColor: 'white',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: '700',
                color: '#10b981',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {moistureMax}%
              </div>
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.75rem'}}>
              <span style={{fontWeight: '600'}}>0%</span>
              <span style={{color: '#ef4444', fontWeight: '600'}}>Critique</span>
              <span style={{color: '#10b981', fontWeight: '600'}}>Normal</span>
              <span style={{fontWeight: '600'}}>100%</span>
            </div>
          </div>
        </div>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '2rem'}}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              border: '2px solid var(--border-light)',
              borderRadius: '0.5rem',
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              fontSize: '0.9375rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
              e.currentTarget.style.borderColor = 'var(--text-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'var(--border-light)';
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 2rem',
              border: 'none',
              borderRadius: '0.5rem',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontSize: '0.9375rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(99, 102, 241, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
              }
            }}
          >
            <Save style={{width: '1.125rem', height: '1.125rem'}} />
            <span>{loading ? 'Sauvegarde en cours...' : 'Sauvegarder les Paramètres'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThresholdSettings;
